import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {BundleForStore} from "../../api/model/bundle";
import {
    CommonBundleProps,
    updateBundleOnDeleteAgency,
    updateBundlePropsOnCreateOrUpdate,
    UpdateDataOnCreateUpdateBundle,
    updateHideLoadBundleInformation
} from "./common";
import cloneDeep from "lodash/cloneDeep";
import {Customer} from "../../api/model/customer";
import {checkForEmptyCustomer, isReturnActivityOfAnotherActivity} from "../../utils/check/common";
import {AgencyProps} from "../../api/model/agency";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps
} from "../../utils/check/type-guards";
import {removeActivityFromTravelComponents} from "./travel-components-utils";

export interface TravelComponentsState extends CommonBundleProps {
    activities: AllActivityTypesArray,
    queueForDeleteActivity: {
        [key: string]: { // ID of Activity
            index: number,
            activity: AllActivityTypes
        }
    }
}

export const DEFAULT_TRAVEL_COMPONENTS = {
    bundleId: null,
    name: null,
    description: null,
    activities: [],
    customer: null,
}

const initialState: TravelComponentsState = {
    ...DEFAULT_TRAVEL_COMPONENTS,
    // activities: JSON.parse('[{"common":{"activityType":"hotel","travelOptionType":"accommodation","day":null,"editId":null,"hideLoadBundleInformation":false,"groupId":null,"id":"eba42e31-70af-437b-8415-717918d4e61c","semanticId":"eba42e31-70af-437b-8415-717918d4e61c","originalRequest":[{"hotelName":"Florence","city":"Daylesford","region":null,"country":"AUS","index":0,"type":"hotel","hotelClass":4,"roomCategory":"DS","numberOfRooms":1,"numberOfNights":1,"numberOfPeople":5,"mealPlan":null}],"request":[{"hotelName":"Florence","city":"Daylesford","region":null,"country":"AUS","index":0,"type":"hotel","hotelClass":4,"roomCategory":"DS","numberOfRooms":1,"numberOfNights":1,"numberOfPeople":5,"mealPlan":null}],"totalEmissions":[{"index":0,"status":"SUCCESS","emissionKgCO2e":39.1875,"scope":"COUNTRY","errorCodes":null,"type":"hotel","errors":null}],"originalTotalEmissions":[{"index":0,"status":"SUCCESS","emissionKgCO2e":39.1875,"scope":"COUNTRY","errorCodes":null,"type":"hotel","errors":null}]},"hotel":{"id":1210873,"name":"Florence","city":"Daylesford","alpha2":"AU","alpha3":"AUS","stars":"FOUR","countryName":"Australia"},"city":{"id":1,"name":"Daylesford","alpha2":"AU","alpha3":"AUS","countryName":"Australia"},"country":{"id":1,"alpha2":"AU","alpha3":"AUS","name":"Australia"},"hotelClass":"FOUR","roomCategory":"DS","hotelStayDuration":{"dates":null,"daysOrNights":1},"originalHotelStayDuration":{"dates":null,"daysOrNights":1},"guestCount":5,"numberOfRooms":1,"mealPlan":null,"informationFromLoadedBundle":null,"backendId":null}]'),
    userId: null,
    agency: null,
    created: null,
    updated: null,
    queueForDeleteActivity: {},
};

export const travelComponents = createSlice({
    name: 'travelComponents',
    initialState,
    reducers: {
        updateTravelComponentsOnDeleteAgency: (state, action: PayloadAction<AgencyProps>) => updateBundleOnDeleteAgency(state, action.payload),
        deleteActivityFromTravelComponents: (state, action: PayloadAction<{
            id: string,
            onlyDeleteReturnActivity: boolean
        }>) => {
            const {id, onlyDeleteReturnActivity} = action.payload
            const index = state.activities.findIndex(activity => activity.common.id === id)
            let updatedActivities = cloneDeep(state.activities)
            if (index > -1) {
                const activity = updatedActivities.at(index)
                updatedActivities.splice(index, 1)
                if (activity && !onlyDeleteReturnActivity) {
                    const {common: {id}} = activity
                    state.queueForDeleteActivity[id] = {index, activity}
                    // one of the returned Activities here is already deleted via updatedActivities.splice(index, 1)
                    // both are tried to delete in order to avoid checking which one is still there etc.
                    updatedActivities = removeActivityFromTravelComponents(updatedActivities, state, activity, index)
                }
            }
            state.activities = updatedActivities
        },
        undoDeleteActivityFromTravelComponents: (state, action: PayloadAction<{
            index: number,
            id: string
        }>) => {
            const {payload: {id, index}} = action
            const item = state.queueForDeleteActivity[id]
            if (item) {
                const {activity} = item
                state.activities.splice(index, 0, activity)
                delete state.queueForDeleteActivity[id]
                if (isActivityWithReturnRouteProps(activity)) {
                    const {returnId} = activity
                    if (returnId) {
                        const returnItem = state.queueForDeleteActivity[returnId]
                        if (returnItem) {
                            const {activity: returnActivity, index: returnIndex} = returnItem
                            state.activities.splice(returnIndex, 0, returnActivity)
                            delete state.queueForDeleteActivity[returnId]
                        }

                    }
                }
            }
        },
        deleteActivityFromTravelComponentsQueue: (state, action: PayloadAction<string>) => {
            const id = action.payload
            const item = state.queueForDeleteActivity[id]
            if (item) {
                delete state.queueForDeleteActivity[id]
                const {activity} = item
                if (isActivityWithReturnRouteProps(activity)) {
                    const {returnId} = activity
                    if (returnId && returnId in state.queueForDeleteActivity) delete state.queueForDeleteActivity[returnId]
                }
            }
        },
        addActivityToTravelComponents: (state, action: PayloadAction<AllActivityTypes>) => {
            state.activities.push(action.payload)
        },
        updateActivityInTravelComponents: (state, action: PayloadAction<{
            activity: AllActivityTypes,
            id: string
        }>) => {
            const {id, activity} = action.payload
            const activityIndexToUpdate = state.activities.map(activity => activity.common.id).indexOf(id)
            if (activityIndexToUpdate > -1) {
                state.activities[activityIndexToUpdate] = activity
            } else {
                if (isReturnActivityOfAnotherActivity(activity, state.activities)) state.activities.push(activity)
            }
        },
        loadTravelComponents: (state, action: PayloadAction<BundleForStore>) => state = {
            ...state,
            ...action.payload
        },
        replaceTravelComponentsOnLoadTravelComponents: (state, action: PayloadAction<BundleForStore>) => {
            const {
                bundleId,
                name,
                description,
                agency,
                customer,
                activities,
                created,
                updated,
            } = action.payload
            state.bundleId = bundleId
            state.name = name
            state.description = description
            state.agency = agency
            state.customer = customer
            state.activities = activities
            state.created = created
            state.updated = updated
            state.activities = activities
        },
        attachTravelComponentsOnLoadTravelComponents: (state, action: PayloadAction<BundleForStore>) => {
            const {activities: activitiesForAttach} = action.payload
            const updatedActivitiesForAttach = cloneDeep(activitiesForAttach)
            updatedActivitiesForAttach.forEach(activity => {
                const {common: {activityType}} = activity
                switch (activityType) {
                    case ACTIVITY_TYPE.hotel:
                        if (isActivityHotelState(activity)) activity.backendId = null
                        break;
                    case ACTIVITY_TYPE.car_bus:
                        if (isActivityCarBusState(activity)) activity.backendId = null
                        break;
                    case ACTIVITY_TYPE.flight:
                        if (isActivityFlightState(activity)) activity.legs.forEach(leg => leg.backendId = null)
                        break;
                    default:
                        activityType satisfies never;
                }
            })
            const updatedActivities = cloneDeep(state.activities)
            updatedActivitiesForAttach.forEach(activity => updatedActivities.push(activity))
            state.activities = updatedActivities
        },
        changeTravelComponentsName: (state, action: PayloadAction<string>) => {
            const newName = action.payload
            if (newName.length === 0) {
                state.name = null
            } else {
                state.name = action.payload
            }
        },
        changeTravelComponentsDescription: (state, action: PayloadAction<string>) => {
            const newDescription = action.payload
            if (newDescription.length === 0) {
                state.description = null
            } else {
                state.description = action.payload
            }
        },
        updateTravelComponentsProps: (state, action: PayloadAction<UpdateDataOnCreateUpdateBundle>) => {
            return updateBundlePropsOnCreateOrUpdate(action.payload, state) as TravelComponentsState
        },
        deleteTravelComponents: (state, action: PayloadAction<AllActivityTypesArray | undefined>) => {
            const {payload} = action
            if (typeof payload === 'undefined') return {...initialState}
            const idsToRemove = new Set(payload.map(activity => activity.common.id))
            state.activities = state.activities.filter(activity => !idsToRemove.has(activity.common.id))
        },
        updateTravelComponentsCustomer: (state, action: PayloadAction<Customer | null>) => {
            state.customer = checkForEmptyCustomer(action.payload)
        },
        setTravelComponentsHideLoadBundleInformation: (state, action: PayloadAction<{
            activityId: string,
            value: boolean
        }>) => {
            const {activityId, value} = action.payload
            state.activities = updateHideLoadBundleInformation(state.activities, activityId, value)
        }
    }
});

export const {
    deleteActivityFromTravelComponents,
    deleteActivityFromTravelComponentsQueue,
    undoDeleteActivityFromTravelComponents,
    addActivityToTravelComponents,
    updateActivityInTravelComponents,
    loadTravelComponents,
    replaceTravelComponentsOnLoadTravelComponents,
    attachTravelComponentsOnLoadTravelComponents,
    changeTravelComponentsName,
    changeTravelComponentsDescription,
    updateTravelComponentsProps,
    deleteTravelComponents,
    updateTravelComponentsCustomer,
    updateTravelComponentsOnDeleteAgency,
    setTravelComponentsHideLoadBundleInformation
} = travelComponents.actions;
export default travelComponents.reducer;